/**
 * @file Dialog 模态对话框
 * @description 居中弹层 + 遮罩；支持 Esc / 点遮罩关闭；开关带 fade。
 *
 * @whenToUse 确认操作、表单弹窗、Showcase Prompt 预览。
 * @whenNotToUse
 * 1. 右侧详情编辑（用 Drawer）。
 * 2. 轻提示（用 Toast）。
 *
 * 关闭动画期间仍挂载 children；业务弹窗若关闭时会清空外部 state，
 * 须用 `useDialogDeferredContent` 延迟销毁展示数据（见 `.cursor/rules/dialog-deferred-content.mdc`）。
 */

import {
  useEffect,
  useId,
  useState,
  type ReactNode,
} from 'react'
import { createPortal } from 'react-dom'
import { X } from 'lucide-react'
import { cn } from '@/lib/cn.ts'
import { ScrollArea } from '@/components/ui/scroll-area.tsx'

/** Dialog 关闭 fade 时长（ms），与 `transition-opacity duration-200` 一致 */
export const DIALOG_CLOSE_ANIMATION_MS = 200

export type DialogProps = {
  /** 是否打开 */
  open: boolean
  /** 打开状态变化 */
  onOpenChange: (open: boolean) => void
  /** 标题 */
  title: string
  /** 主内容 */
  children: ReactNode
  /** 底部操作区 */
  footer?: ReactNode
  /** 内容区最大宽度 class，默认 max-w-lg */
  className?: string
  /** 内容区域自定义类名 */
  contentClassName?: string
}

/**
 * 模态对话框（Portal + fade）
 */
export function Dialog({
  open,
  onOpenChange,
  title,
  children,
  footer,
  className,
  contentClassName
}: DialogProps) {
  const titleId = useId()
  const [mounted, setMounted] = useState(open)
  /** 始终从不可见起步，避免与 open 同步初始化时首帧已是 opacity-100 */
  const [visible, setVisible] = useState(false)

  useEffect(() => {
    if (open) {
      setVisible(false)
      setMounted(true)
      return
    }
    setVisible(false)
    const timer = window.setTimeout(() => setMounted(false), DIALOG_CLOSE_ANIMATION_MS)
    return () => window.clearTimeout(timer)
  }, [open])

  // 双 rAF：先让 opacity-0 完成绘制，再切到 opacity-100 才能触发 transition
  useEffect(() => {
    if (!mounted || !open) return
    let raf2 = 0
    const raf1 = requestAnimationFrame(() => {
      raf2 = requestAnimationFrame(() => setVisible(true))
    })
    return () => {
      cancelAnimationFrame(raf1)
      cancelAnimationFrame(raf2)
    }
  }, [mounted, open])

  useEffect(() => {
    if (!open) return
    const onKey = (event: KeyboardEvent) => {
      if (event.key === 'Escape') onOpenChange(false)
    }
    window.addEventListener('keydown', onKey)
    const prev = document.body.style.overflow
    document.body.style.overflow = 'hidden'
    return () => {
      window.removeEventListener('keydown', onKey)
      document.body.style.overflow = prev
    }
  }, [open, onOpenChange])

  if (!mounted || typeof document === 'undefined') return null

  return createPortal(
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      <button
        type="button"
        aria-label="关闭弹层"
        className={cn(
          'absolute inset-0 bg-foreground/30 backdrop-blur-xs transition-opacity duration-200',
          visible ? 'opacity-100' : 'opacity-0',
        )}
        onClick={() => onOpenChange(false)}
      />
      <div
        role="dialog"
        aria-modal="true"
        aria-labelledby={titleId}
        className={cn(
          'relative z-10 flex w-full flex-col rounded-2xl border border-border bg-sidebar transition-opacity duration-200',
          visible ? 'opacity-100' : 'opacity-0',
          className ?? 'max-w-lg',
        )}
      >
        <div className="flex items-center justify-between gap-2 border-b border-border px-4 py-2">
          <h2 id={titleId} className="text-[15px] font-medium tracking-tight">
            {title}
          </h2>
          <button
            type="button"
            aria-label="关闭"
            className="inline-flex size-6.5 items-center justify-center rounded-lg text-muted transition-all duration-200 hover:opacity-90 hover:bg-foreground/10 focus-visible:opacity-90"
            onClick={() => onOpenChange(false)}
          >
            <X className="size-4" aria-hidden />
          </button>
        </div>
        <ScrollArea className={cn('min-h-0 flex-1 px-4 py-2', contentClassName)}>{children}</ScrollArea>
        {footer ? (
          <div className="flex items-center justify-end gap-2 border-t border-border px-4 py-2">
            {footer}
          </div>
        ) : null}
      </div>
    </div>,
    document.body,
  )
}
